import React, { useState, useEffect } from 'react'
import { IFile } from 'types'
import api, { apiPrefix } from 'helpers/api'
import { getAuthToken } from 'helpers/auth'
import Button from 'components/button'
import DeleteResource from 'components/delete'
import { useApplicationStore } from 'hooks/application'
import Spinner from 'components/spinner'

interface PreviewProps {
  file: IFile
  fileIndex: number
  setFileIndex: (newFileIndex: number) => void
  firstFile: boolean
  lastFile: boolean
  visible: boolean
  tableId: string
  isAdmin: boolean
  isContributor: boolean
  isReadOnly: boolean
  handleRemoveFile: (index: number) => Promise<void>
  handleAddFile: () => void
  previewRef: React.RefObject<HTMLDivElement | null>
  columnId?: string
}

const Preview: React.FC<PreviewProps> = ({
  file,
  fileIndex,
  setFileIndex,
  firstFile,
  lastFile,
  visible,
  tableId,
  isAdmin,
  isContributor,
  isReadOnly,
  handleRemoveFile,
  handleAddFile,
  previewRef,
  columnId
}) => {
  const { displayErrorMessage } = useApplicationStore()

  const [ready, setReady] = useState(false)
  const [done, setDone] = useState(false)
  const [errorMessage, setErrorMessage] = useState()
  const [src, setSrc] = useState<string>('about:blank')

  const [deleteResourceModal, setDeleteResourceModal] = useState<boolean>(false)

  const isVideo = /^video\//.test(file.mimetype)
  const isAudio = /^audio\//.test(file.mimetype)
  const isPdf = /^application\/pdf$/.test(file.mimetype)
  const isImage = /^image\//.test(file.mimetype)
  const isCsv = /^(text\/csv|application\/csv|application\/vnd\.ms-excel)$/i.test(file.mimetype)
  const isHtml = /^text\/html$/.test(file.mimetype)
  const isText = (/^text\//.test(file.mimetype) || isCsv) && !isHtml

  const canPreview = isVideo || isAudio || isPdf || isImage || isText

  useEffect(() => {
    return () => {
      if (src?.startsWith('blob:')) URL.revokeObjectURL(src)
    }
  }, [src])

  const renderIframe = async () => {
    const token = getAuthToken()
    if (ready || !token) return

    let fullUrl = ''
    if (file.version === 1) {
      const url =
        `/table/${tableId}/file` +
        encodeURIComponent(
          JSON.stringify({
            columnId,
            filename: file.filename
          })
        )
      fullUrl = `${apiPrefix}${url}`
    } else {
      try {
        const fileCall = await api.signUrl(file.url)
        fullUrl = fileCall.data.url
      } catch (err) {
        displayErrorMessage(err)
        setReady(false)
        return
      }
    }

    const xhr = new XMLHttpRequest()
    xhr.open('GET', fullUrl)
    xhr.responseType = 'blob'
    xhr.setRequestHeader('Authorization', token)
    xhr.send()

    xhr.onload = () => {
      if (xhr.readyState === xhr.DONE) {
        if (xhr.status === 200) {
          const previewMime = isCsv ? 'text/plain' : file.mimetype
          const typedBlob = new Blob([xhr.response], { type: previewMime })
          const url = URL.createObjectURL(typedBlob)
          setSrc(url)
          setReady(true)
        } else {
          setReady(false)
          xhr.response.text().then((text: any) => {
            try {
              const response = JSON.parse(text)
              if (response.detail) {
                setErrorMessage(response.detail.message)
              } else {
                setErrorMessage(text)
              }
            } catch {
              setErrorMessage(text)
            }
          })
        }
        setDone(true)
      }
    }
  }

  const setFullScreen = () => {
    const element = previewRef.current

    if (!element) {
      displayErrorMessage("This preview can't be shown full screen. Close it and open it again.")
      return
    }

    if (document.fullscreenElement) {
      document.exitFullscreen().catch((error) => {
        displayErrorMessage(error)
      })
    } else {
      // Check if requestFullscreen method exists before calling it
      // iOS Safari and some mobile browsers don't support the Fullscreen API
      if (typeof element.requestFullscreen === 'function') {
        element.requestFullscreen().catch((error) => {
          displayErrorMessage(error)
        })
      } else {
        displayErrorMessage("Your browser or device doesn't support full screen.")
      }
    }
  }
  useEffect(() => {
    renderIframe()
  }, [])

  const canAddFiles = (isAdmin || isContributor) && !isReadOnly
  const canDeleteFiles = (isAdmin || isContributor) && !isReadOnly
  const canDownloadFiles = ready && (isAdmin || isContributor || isReadOnly)

  /* eslint-disable jsx-a11y/media-has-caption */
  const audioNode = isAudio && isAudio && (
    <audio className="w-full" controls>
      <source src={src} />
    </audio>
  )

  const videoNode = isVideo && (
    <video className="w-full h-full object-contain" src={src} controls>
      <source src={src} />
    </video>
  )
  /* eslint-disable jsx-a11y/media-has-caption */
  return (
    <div className={`${visible ? 'flex' : 'hidden'} w-full h-full flex-col items-center justify-center `}>
      <div className="flex items-center justify-center w-full mb-5">
        <Button disabled={firstFile} onClick={() => setFileIndex(fileIndex - 1)} className="mr-auto">
          Previous
        </Button>
        <Button onClick={() => setFullScreen()}>Toggle Fullscreen</Button>
        {canDownloadFiles && (
          <a
            href={src}
            download={file.filename}
            className={`${!ready ? 'pointer-events-none cursor-not-allowed' : 'pointer-events-auto cursor-default'}`}
          >
            <Button className="ml-2.5" isLoading={!done}>
              Download
            </Button>
          </a>
        )}
        {canAddFiles && (
          <Button className="ml-2.5" internalType="accept" onClick={() => handleAddFile()}>
            Add
          </Button>
        )}
        {canDeleteFiles && (
          <Button className="ml-2.5" internalType="danger" onClick={() => setDeleteResourceModal(true)}>
            Delete
          </Button>
        )}
        <Button disabled={lastFile} className="ml-auto" onClick={() => setFileIndex(fileIndex + 1)}>
          Next
        </Button>
      </div>

      {done && ready && canPreview && (
        <div className="flex items-center justify-center w-full h-[calc(100%_-_60px)]">
          {videoNode}
          {audioNode}
          {isPdf && (
            <object
              className="w-full h-full"
              data={src}
              type={file.mimetype}
              aria-label={`Preview of ${file?.filename ?? 'PDF document'}`}
            >
              {/* Fallback for SRs / browsers without PDF support */}
              <p className="p-4">
                Can’t display this PDF.{' '}
                <a href={src} target="_blank" rel="noopener noreferrer">
                  Open or download the file
                </a>
                .
              </p>
            </object>
          )}
          {isImage && <img className="w-full h-full object-contain" src={src} alt={file.filename} />}
          {isText && (
            <iframe sandbox="" referrerPolicy="no-referrer" className="w-full h-full" src={src} title={file.filename} />
          )}
        </div>
      )}

      {done && !ready && (
        <div className="flex items-center justify-center w-full h-[calc(100%_-_60px)]">
          <span className="text-red">
            {errorMessage || "This file couldn't be loaded. Close the preview and try again."}
          </span>
        </div>
      )}

      {done && ready && !canPreview && (
        <div className="flex items-center justify-center w-full h-[calc(100%_-_60px)]">
          <span className="text-red">File type not supported for preview</span>
        </div>
      )}

      {!done && (
        <div className="flex items-center justify-center h-[calc(100%_-_60px)]">
          <Spinner className="w-[50px] h-[50px]" />
        </div>
      )}

      {deleteResourceModal && (
        <DeleteResource
          id="delete-file-modal"
          open={deleteResourceModal}
          setOpen={setDeleteResourceModal}
          resourceName={file.filename}
          deleteResource={() => handleRemoveFile(fileIndex)}
        />
      )}
    </div>
  )
}

export default Preview
